/**
 * Floating in-app support chat widget — bottom-right.
 *
 * Connects to `/api/chatbot/ws/{session_id}` over WebSocket once the user
 * opens it. Sends typing pings, receives messages from bot/admin/system,
 * shows a typing indicator. Auth uses an access token grabbed from cookies
 * (we use a short-lived re-issued JWT exclusively for WS auth via
 * `/auth/ws-token`, since browsers can't send cookies on WS cross-origin).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, User as UserIcon, Sparkles, Headset } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import Linkified from "@/components/Linkified";

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [greeting, setGreeting] = useState("");
    const [status, setStatus] = useState("bot");        // bot | admin_takeover
    const [typing, setTyping] = useState(null);          // "bot" | "admin" | null
    const [wsState, setWsState] = useState("connecting"); // connecting | open | closed
    const wsRef = useRef(null);
    const scrollRef = useRef(null);
    const typingTimer = useRef(null);
    const reconnectTimer = useRef(null);
    const reconnectAttempts = useRef(0);
    const closingByChoice = useRef(false);

    // Bootstrap: fetch config, lazily on first open
    const bootstrap = useCallback(async () => {
        try {
            const { data: cfg } = await api.get("/chatbot/config");
            if (!cfg.enabled) { setEnabled(false); return; }
            setGreeting(cfg.greeting);
            const { data: s } = await api.post("/chatbot/session/start");
            setSessionId(s.session_id);
            setStatus(s.status);
            // Load history
            try {
                const { data: hist } = await api.get(`/chatbot/sessions/${s.session_id}/messages`);
                setMessages(hist.messages || []);
                setStatus(hist.status || "bot");
            } catch (_) { /* empty session */ }
        } catch (e) { toast.error(apiError(e)); }
    }, []);

    useEffect(() => {  // eslint-disable-line
        if (open && !sessionId) bootstrap();
    }, [open, sessionId, bootstrap]);

    // WebSocket lifecycle — open when session_id ready, auto-reconnect on close.
    // Without reconnect, the widget silently breaks after the first idle-disconnect
    // (network blip, backend hot-reload, proxy timeout). User sees "send does nothing".
    const connectWs = useCallback(async () => {
        if (!sessionId) return;
        setWsState("connecting");
        let token = "";
        try {
            const { data } = await api.post("/auth/ws-token");
            token = data.token;
        } catch (_) { setWsState("closed"); return; }
        const wsBase = API.replace(/^http/, "ws").replace(/\/api$/, "");
        const ws = new WebSocket(`${wsBase}/api/chatbot/ws/${sessionId}?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onopen = async () => {
            const wasReconnect = reconnectAttempts.current > 0;
            reconnectAttempts.current = 0;
            setWsState("open");
            // Re-sync any messages that may have arrived while we were disconnected.
            // Diff against current state so we can show "you missed N messages" toast.
            try {
                const { data: hist } = await api.get(`/chatbot/sessions/${sessionId}/messages`);
                if (hist?.messages) {
                    setMessages(prev => {
                        if (wasReconnect) {
                            const seen = new Set(prev.map(m => m.id));
                            const missed = hist.messages.filter(m => !seen.has(m.id) && m.role !== "user").length;
                            if (missed > 0) {
                                toast.success(`Welcome back · ${missed} new message${missed > 1 ? "s" : ""} from your assistant`,
                                    { duration: 4000 });
                            } else {
                                toast.success("Reconnected · you're all caught up", { duration: 2500 });
                            }
                        }
                        return hist.messages;
                    });
                }
                if (hist?.status) setStatus(hist.status);
            } catch (_) { /* ignore */ }
        };
        ws.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                if (data.type === "message") {
                    setMessages(prev => {
                        // Dedupe by id (we may also receive the same message via re-sync on reconnect)
                        if (prev.some(m => m.id === data.message.id)) return prev;
                        return [...prev, data.message];
                    });
                    setTyping(null);
                    if (data.message.role === "system") {
                        setStatus(data.message.content.includes("human") ? "admin_takeover" : "bot");
                    }
                } else if (data.type === "typing") {
                    if (data.from !== "user") {
                        setTyping(data.from);
                        clearTimeout(typingTimer.current);
                        typingTimer.current = setTimeout(() => setTyping(null), 4000);
                    }
                }
            } catch (_) { /* ignore */ }
        };
        ws.onerror = () => { /* onclose will fire next */ };
        ws.onclose = () => {
            wsRef.current = null;
            if (closingByChoice.current) { setWsState("closed"); return; }
            setWsState("closed");
            // Exponential backoff: 1s, 2s, 4s, 8s, max 15s
            const delay = Math.min(15000, 1000 * Math.pow(2, reconnectAttempts.current));
            reconnectAttempts.current += 1;
            clearTimeout(reconnectTimer.current);
            reconnectTimer.current = setTimeout(connectWs, delay);
        };
    }, [sessionId]);

    useEffect(() => {
        if (!sessionId) return;
        closingByChoice.current = false;
        connectWs();
        return () => {
            closingByChoice.current = true;
            clearTimeout(reconnectTimer.current);
            if (wsRef.current) wsRef.current.close();
        };
    }, [sessionId, connectWs]);

    // Auto-scroll on new message
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, typing]);

    const send = (e) => {
        e?.preventDefault();
        const text = draft.trim();
        if (!text) return;
        if (!wsRef.current || wsRef.current.readyState !== 1) {
            toast.error(wsState === "closed"
                ? "Reconnecting… your message will go through once we're back online."
                : "Still connecting — try again in a moment.");
            return;
        }
        wsRef.current.send(JSON.stringify({ type: "message", content: text }));
        setDraft("");
    };

    const onTyping = (e) => {
        setDraft(e.target.value);
        if (wsRef.current && wsRef.current.readyState === 1) {
            wsRef.current.send(JSON.stringify({ type: "typing" }));
        }
    };

    if (!enabled) return null;

    return (
        <>
            {/* Launcher */}
            {!open && (
                <button onClick={() => setOpen(true)} data-testid="chat-widget-open"
                    className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary hover:bg-primary text-primary-foreground rounded-full shadow-2xl shadow-primary/30 flex items-center justify-center transition-transform hover:scale-105"
                    aria-label="Open support chat">
                    <MessageCircle className="w-6 h-6" strokeWidth={2}/>
                </button>
            )}

            {open && (
                <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-background border border-border shadow-2xl flex flex-col"
                     data-testid="chat-widget-panel">
                    <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary/20 border border-primary/40 flex items-center justify-center">
                                {status === "admin_takeover"
                                    ? <Headset className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                                    : <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5}/>}
                            </div>
                            <div>
                                <div className="text-sm text-foreground">Law OS Support</div>
                                <div className="text-[10px] text-muted-foreground flex items-center gap-1.5" data-testid="chat-widget-status">
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${wsState === "open" ? "bg-emerald-400" : wsState === "connecting" ? "bg-amber-400 animate-pulse" : "bg-rose-400"}`}/>
                                    {wsState === "open"
                                        ? (status === "admin_takeover" ? "A human agent is here" : "Product Support · online")
                                        : wsState === "connecting" ? "Connecting…" : "Reconnecting…"}
                                </div>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} data-testid="chat-widget-close"
                            className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
                    </header>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
                        {messages.length === 0 && greeting && (
                            <Bubble role="bot" content={greeting}/>
                        )}
                        {messages.map(m => <Bubble key={m.id} role={m.role} content={m.content} author={m.author_name}/>)}
                        {typing && (
                            <div className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="chat-typing">
                                <div className="w-7 h-7 bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
                                    {typing === "admin" ? <Headset className="w-3 h-3 text-primary"/> : <Sparkles className="w-3 h-3 text-primary"/>}
                                </div>
                                <span className="italic">{typing === "admin" ? "Agent" : "Assistant"} is typing<span className="dots">…</span></span>
                            </div>
                        )}
                    </div>

                    <form onSubmit={send} className="border-t border-border p-3 flex gap-2">
                        <input value={draft} onChange={onTyping} placeholder="Ask anything…"
                            data-testid="chat-widget-input"
                            className="flex-1 bg-muted border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                        <button type="submit" disabled={!draft.trim()} data-testid="chat-widget-send"
                            className="bg-primary text-primary-foreground px-3 disabled:opacity-30 hover:bg-primary">
                            <Send className="w-4 h-4"/>
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}

function Bubble({ role, content, author }) {
    const isUser = role === "user";
    const isSystem = role === "system";
    if (isSystem) {
        return (
            <div className="text-center text-[10px] text-primary uppercase tracking-[0.16em] py-1" data-testid="chat-bubble-system">
                {content}
            </div>
        );
    }
    return (
        <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`chat-bubble-${role}`}>
            <div className={`w-7 h-7 shrink-0 flex items-center justify-center border ${
                isUser ? "bg-muted border-border" : "bg-primary/20 border-primary/40"
            }`}>
                {isUser ? <UserIcon className="w-3.5 h-3.5 text-muted-foreground"/>
                    : role === "admin" ? <Headset className="w-3.5 h-3.5 text-primary"/>
                    : <Sparkles className="w-3.5 h-3.5 text-primary"/>}
            </div>
            <div className={`max-w-[78%] ${isUser ? "text-right" : ""}`}>
                <Linkified text={content} as="div" className={`px-3 py-2 text-sm whitespace-pre-wrap rounded-lg ${
                    isUser ? "bg-primary text-primary-foreground border border-primary/40"
                          : "bg-card border border-border text-foreground"
                }`} />
                {!isUser && author && <div className="text-[9px] text-muted-foreground mt-1 px-1">{author}</div>}
            </div>
        </div>
    );
}
